import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { endOfMonth, endOfWeek } from "date-fns";
import { useRecoilValue } from "recoil";

import { ReactComponent as CaretDownIcon } from "assets/icons/caret-down.svg";
import { ReactComponent as ClockIcon } from "assets/icons/clock.svg";
import { ReactComponent as ConnectIcon } from "assets/icons/connect.svg";
import { useLogout } from "hooks/apis/auth";
import useInactivityLogout from "hooks/useInactivityLogout";
import format from "lib/format";
import { userState } from "store/store";
import ButtonSmall from "./ButtonSmall";
import NiceModal from "@ebay/nice-modal-react";
import SimpleModal from "./modals/SimpleModal";

const formatTimeRemaining = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000); // Convert milliseconds to seconds
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes < 10 ? `0${minutes}` : minutes}분 ${seconds < 10 ? `0${seconds}` : seconds}초`; // Ensure two digits for seconds
};

const Header = () => {
  const user = useRecoilValue(userState);
  const logoutMutation = useLogout();
  const { timeRemaining, resetTimer } = useInactivityLogout();

  const logout = () => {
    NiceModal.show(SimpleModal, {
      title: "로그아웃",
      message: "로그아웃 하시겠습니까?",
      submitText: "로그아웃",
    })
      .then(() => {
        logoutMutation.mutate();
      })
      .catch(() => {});
  };

  return (
    <header className="fixed z-50 top-0 h-[3.5rem] w-[calc(100%-3.5rem)] bg-white border-b border-secondary px-[0.625rem] flex items-center justify-end">
      <div className="flex items-center gap-[1.875rem]">
        <div className="flex items-center gap-[0.625rem]">
          <div className="flex items-center gap-[0.313rem]">
            <ClockIcon />
            <span className="text-primary">
              {formatTimeRemaining(timeRemaining)}
            </span>
          </div>
          <ButtonSmall
            onClick={resetTimer}
            color="light-gray"
            className="px-[0.625rem]"
          >
            연장
          </ButtonSmall>
        </div>
        <div className="flex items-center gap-[0.313rem]">
          <span
            className={clsx(
              "size-[2.25rem] rounded-md inline-flex justify-center items-center",
              user?.isLinked ? "bg-green" : "bg-red",
            )}
          >
            <ConnectIcon className="size-[1.625rem]" />
          </span>
          <Popover className="relative">
            <PopoverButton className="flex items-center p-[0.438rem] gap-[0.688rem] rounded-md shadow-[0px_4px_3px_0px_rgba(0,0,0,0.25)]">
              <span className="font-bold text-14">
                <strong className="font-bold">{user?.name}님</strong>(
                {user?.userId})
              </span>
              <span className="inline-flex mt-[0.188rem]">
                <CaretDownIcon className="size-[0.875rem]" />
              </span>
            </PopoverButton>
            <PopoverPanel
              anchor="bottom"
              className="z-[100] mt-[0.688rem] flex flex-col w-80 bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.25)] p-[1.875rem] pb-[1.313rem] rounded-b-lg gap-5"
            >
              <div>
                <span className="text-20">
                  <strong className="font-bold">{user?.name}</strong>님
                </span>
                <span className="text-14 secondary">({user?.userId})</span>
              </div>
              <dl className="grid grid-cols-[auto_1fr] text-14 font-medium">
                <dt className="relative text-secondary py-[0.625rem] after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  현재 등급
                </dt>
                <dd className="text-right py-[0.625rem] relative  after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  {user?.grade?.name?.toUpperCase?.()}
                </dd>
                <dt className="relative text-secondary py-[0.625rem] after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  일일 답변 현황
                </dt>
                <dd className="text-right py-[0.625rem] relative  after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  {user?.dayCount?.toLocaleString?.()}건
                </dd>
                <dt className="relative text-secondary py-[0.625rem] after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  주간 답변 현황 ({format(user!.startWeek, "M.d")}~
                  {format(endOfWeek(user!.startWeek!), "M.d")})
                </dt>
                <dd className="text-right py-[0.625rem] relative  after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  {user?.weekCount?.toLocaleString?.()}건
                </dd>
                <dt className="relative text-secondary py-[0.625rem] after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  월간 답변 현황 ({format(user!.startMonth, "M.d")}~
                  {format(endOfMonth(user!.startMonth!), "M.d")})
                </dt>
                <dd className="text-right py-[0.625rem] relative  after:absolute after:z-10 after:bg-gray after:h-px after:w-full after:bottom-0 after:left-0">
                  {user?.monthCount?.toLocaleString?.()}건
                </dd>
              </dl>
              <button
                type="button"
                onClick={logout}
                className="ml-auto text-primary font-medium text-14 py-[0.313rem]"
              >
                로그아웃
              </button>
            </PopoverPanel>
          </Popover>
        </div>
      </div>
    </header>
  );
};

export default Header;
